// I PDF disegnati da noi, non dal browser (pdf.js).
//
// Prima un PDF stava in un <iframe> e lo mostrava il visore del browser. Non funziona:
// - su iPhone e su Safari l'iframe di un PDF resta spesso **bianco** (iOS non mostra i PDF dentro un iframe);
// - «pagina 2, ingrandimento 150%» si chiedeva col pezzetto dopo il # (`#page=2&zoom=150`), che ogni browser
//   interpreta come vuole e Safari ignora; per farlo ripartire si cambiava la `key` di React, cioè si **ricaricava
//   tutto il PDF** a ogni clic sulla freccia;
// - il visore del browser ha la sua barra (stampa, scarica, "apri in un'altra scheda") che porta il documento di un
//   cliente fuori dalla nostra pagina e dal registro degli accessi.
//
// Qui il PDF si disegna su una tela (canvas): le pagine e l'ingrandimento sono nostri, e si vede uguale su tutti.
//
// pdf.js pesa più di un mega: si carica **solo quando serve** (import dinamico, un pezzo a parte del pacchetto),
// così chi non apre mai un PDF non se lo scarica.

let libreria = null;

const carica = async () => {
  if (libreria) return libreria;
  const pdfjs = await import('pdfjs-dist/build/pdf');
  // Il "worker" è il pezzo che fa il lavoro pesante in un filo suo, se no la pagina si inchioda su un PDF grosso.
  // L'indirizzo del file lo dà webpack (regola in craco.config.js: quel file esce dal pacchetto come risorsa).
  const { default: indirizzoWorker } = await import('pdfjs-dist/build/pdf.worker.min.js');
  pdfjs.GlobalWorkerOptions.workerSrc = indirizzoWorker;
  libreria = pdfjs;
  return pdfjs;
};

/**
 * Apre un PDF da un Blob. Torna { pagine, disegna, chiudi }.
 *   disegna(canvas, pagina, scala) mette quella pagina sulla tela e torna { larghezza, altezza }.
 * Solleva se il file non è un PDF leggibile: chi chiama ripiega (vedi VisorePdf).
 */
export const apriPdf = async (blob) => {
  const pdfjs = await carica();
  const dati = new Uint8Array(await blob.arrayBuffer());
  // isEvalSupported: false → pdf.js non usa `eval` per i font. Serve a noi, non a una regola: i PDF arrivano da
  // fuori (li carica il cliente) e non c'è motivo di far eseguire codice a un file che stiamo solo guardando.
  const documento = await pdfjs.getDocument({ data: dati, isEvalSupported: false }).promise;
  return {
    pagine: documento.numPages,
    disegna: async (tela, numero, scala = 1) => {
      const pagina = await documento.getPage(Math.min(Math.max(1, numero), documento.numPages));
      // Sugli schermi fitti (telefoni, Retina) si disegna più grande e si rimpicciolisce col CSS: se no il testo
      // di una carta d'identità è una macchia.
      const fitta = Math.min(window.devicePixelRatio || 1, 2);
      const vista = pagina.getViewport({ scale: scala * fitta });
      tela.width = Math.floor(vista.width);
      tela.height = Math.floor(vista.height);
      tela.style.width = `${Math.floor(vista.width / fitta)}px`;
      tela.style.height = `${Math.floor(vista.height / fitta)}px`;
      await pagina.render({ canvasContext: tela.getContext('2d'), viewport: vista }).promise;
      return { larghezza: vista.width / fitta, altezza: vista.height / fitta };
    },
    chiudi: () => { try { documento.destroy(); } catch { /* già chiuso */ } },
  };
};

/** Quante pagine ha un PDF. null = non si è riusciti a leggerlo (chi chiama ha il suo ripiego). */
export const paginePdf = async (blob) => {
  try {
    const aperto = await apriPdf(blob);
    const quante = aperto.pagine;
    aperto.chiudi();
    return quante || null;
  } catch {
    return null;
  }
};
